import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withStyles } from "@material-ui/core/styles";
import { Grid, Divider } from "@material-ui/core";
import styled from "styled-components";
import _ from "lodash";
import moment from "moment";
import { transformNumber } from "../../../../utils/transformNumber";
import { getConfigItemByParentId } from "../../../../store/state/configItems";
import { getAvailableCollabGroups } from "../../../../store/state/fund";
import { getDealStages, getDealStatuses, getFundNames, getBrokers, getDealCompanyContacts } from "../../../../store/state/enumeratedTypes";
import { getContactsByCompany } from "../../../../store/state/contacts";
import { AutoCompleteInput } from "../../../Global/FormikMaterial";
import GridTextInput from "../../../Global/Fields/GridTextInput";
import GridTextArea from "../../../Global/Fields/GridTextArea";
import GridCurrencyInput from "../../../Global/Fields/GridCurrencyInput";
import GridPercentInput from "../../../Global/Fields/GridPercentInput";
import GridDatePicker from "../../../Global/Fields/GridDatePicker";
import { setConfigItemListState, setEnumeratedTypesListState, setEnumeratedFundNames, setEnumeratedTypesContactsListState } from "../../../Global/Fields/util";
import { PROJECT_TYPE } from "../../../../constants/configItemParents";
import { PIPELINE, DEAD, CLOSED, SOLD, REFINANCE } from "../../../../constants/status";
import dealViewMetric from "../../../../constants/screenElements/dealViewMetric";
import DealViewMetricFormatter from "../../../Global/Metric/DealViewMetricFormatter";
import STAGES from "../../../../constants/dealStages";
import AddButton from "../../../Global/Buttons/AddButton";
import { StyledLabel } from "../../../Global/Fields/StyledInputLabel";
import FundSelectAndPercent from "./FundSelectAndPercent";
import { getFilteredDealStatuses } from "../../../../utils/statusWorkflowHelper";
import SimpleInfoDialog from "components/Global/ConfirmationDialogs/SimpleInfoDialog";

const getMetric = (values, metricId) => DealViewMetricFormatter[metricId](values[metricId]);

const { PURCHASE_PRICE, CAP_RATE, NOI, OCCUPANCY, INTERNAL_EQUITY_OWNERSHIP, EXTERNAL_EQUITY_OWNERSHIP } = dealViewMetric.map;

const FundsAddButton = styled(AddButton)`
  && {
    margin-left: 0;
    margin-top: 8px;
  }
`;

const FundsSelectGrid = styled(Grid)`
  && {
    margin-top: ${props => (props.isEditing ? "inherit" : "6px")};
  }

  > div {
    margin-top: ${props => (props.isEditing ? "inherit" : "0px")};
  }
`;

const ViewModeDiv = styled.div`
  font-size: 17px;
  text-align: right;
`;
const styles = () => ({
  heading: {
    "font-size": "20px;"
  },
  disabledInputStyle: {
    color: "rgba(0, 0, 0, 0.87);"
  },
  red: {
    color: "red"
  }
});

class ViewAndEditAssetDetails extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      projectTypes: [],
      brokerCompanies: [],
      dealStatuses: [],
      dealStages: [],
      fundNames: [],
      teamMembers: [],
      showStageField: true,
      searchedFundNames: false,
      showCashflowOverrideWarning: false,
      alreadyShowedCashflowOverrideWarning: false,
    };
  }

  addEmptyFund = () => {
    const { values, setFieldValue } = this.props;
    const { funds } = values;
    const copiedFunds = [...funds];
    copiedFunds.push({
      id: "",
      fundDealBridge: {
        allocationPercent: 100,
        isPrimary: true
      }
    });
    setFieldValue("funds", copiedFunds);
  };

  addEmptyCollaborationGroup = () => {
    const { values, setFieldValue } = this.props;
    const { collaborationGroups } = values;
    const copiedCollaborationGroups = [...collaborationGroups];
    copiedCollaborationGroups.push({
      id: "",
      fundDealBridge: {
        allocationPercent: 100,
        isPrimary: false,
      }
    });
    setFieldValue("collaborationGroups", copiedCollaborationGroups);
  };

  allFundsAssigned = () => {
    return this.props.values.funds.filter(x => x.id).length === this.props.values.funds.length;
  };

  allCollaborationGroupsAssigned = () => {
    return this.props.values.collaborationGroups && this.props.values.collaborationGroups.filter(x => x.id).length === this.props.values.collaborationGroups.length;
  };

  removeFund = index => {
    const { values, setFieldValue } = this.props;
    const { funds } = values;
    const copiedFunds = [...funds];
    copiedFunds.splice(index, 1);
    setFieldValue("funds", copiedFunds);
  };

  removeCollaborationGroup = index => {
    const { values, setFieldValue } = this.props;
    const { collaborationGroups } = values;
    const copiedCollaborationGroups = [...collaborationGroups];
    copiedCollaborationGroups.splice(index, 1);
    setFieldValue("collaborationGroups", copiedCollaborationGroups);
  };

  getDealStatus(dealStatusId) {
    return this.getConfigItemLabel(this.state.dealStatuses, dealStatusId);
  }

  getDealStage(stageId) {
    return this.getConfigItemLabel(this.state.dealStages, stageId);
  }

  getProjectType(projectTypeId) {
    return this.getConfigItemLabel(this.state.projectTypes, projectTypeId);
  }

  getConfigItemLabel(configItems, id) {
    const found = _.find(configItems, x => x.value === id);
    if (found) return found.label;
    return "--";
  }

  getBrokerCompany(id) {
    return this.getConfigItemLabel(this.state.brokerCompanies, id);
  }

  getBrokerContacts(id) {
    const { values, brokerContacts } = this.props;
    let contacts = [];
    if (this.hasBrokerContacts(values.brokerCompanyId, brokerContacts)) {
      contacts = brokerContacts[values.brokerCompanyId].map(x => ({
        label: `${x.firstName} ${x.lastName}`,
        value: x.id
      }));
    }

    return this.getConfigItemLabel(contacts, id);
  }

  componentDidMount() {
    this.props.getConfigItemByParentId({ id: PROJECT_TYPE });
    this.props.getDealStatuses();
    this.props.getDealStages();
    this.props.getBrokers();
    this.props.getAvailableCollabGroups({dealId: this.props.values.id});
    this.props.getDealCompanyContacts({ dealId: this.props.values.id });

    if (this.props.values.brokerCompanyId) {
      this.props.getContactsByCompany({ companyId: this.props.values.brokerCompanyId });
    }

    this.checkToShowStageDropdown();
  }

  checkToShowStageDropdown() {
    const { dealStatusId } = this.props.values;
    if (dealStatusId === PIPELINE || dealStatusId === DEAD) {
      this.setState({
        showStageField: true
      });
    } else {
      this.setState({
        showStageField: false
      });
    }
  }

  componentDidUpdate = (prevProps, prevState) => {
    if(!this.props.isEditing && this.state.alreadyShowedCashflowOverrideWarning){
      this.setState({showCashflowOverrideWarning: false, alreadyShowedCashflowOverrideWarning: false});
    }
    
    const previousDealStatusId = prevProps.values.dealStatusId;
    const currentDealStatusId = this.props.values.dealStatusId;
    if (this.props.configItems && this.props.configItems.configItemByParent) {
      setConfigItemListState(PROJECT_TYPE, "projectTypes", this);
    }

    if (this.props.enumeratedTypes) {
      setEnumeratedTypesListState("deal_statuses", "dealStatuses", this);
      setEnumeratedTypesListState("deal_stages", "dealStages", this);
      setEnumeratedTypesListState("brokers", "brokerCompanies", this);
      setEnumeratedTypesContactsListState("deal_company_contacts", "teamMembers", this);
      setEnumeratedFundNames("fund_names", "fundNames", this, "funds");
    }

    if (previousDealStatusId !== currentDealStatusId) {
      this.checkToShowStageDropdown();
    }
    const prevSelectedBrokerId = prevProps.values.brokerCompanyId;
    if (this.props.values.brokerCompanyId !== prevSelectedBrokerId) {
      this.props.getContactsByCompany({ companyId: this.props.values.brokerCompanyId });
    }

    if (
      !this.state.searchedFundNames &&
      this.props.values &&
      this.props.values.funds &&
      this.props.values.funds.length > 0 &&
      this.state.fundNames.length === 0
    ) {
      this.props.getFundNames({ companyId: this.props.values.funds[0].companyId });
      this.setState({ searchedFundNames: true });
    }
  };

  hasBrokerContacts = (selectedBrokerId, brokerContacts) =>
    selectedBrokerId && brokerContacts && brokerContacts[selectedBrokerId] && brokerContacts[selectedBrokerId].length > 0;

  getFormattedDate = value => {
    if (value) return moment(value).format("MM/DD/YYYY");
    return "";
  };

  closeCashfllowOverrideWarningModal = () => {
    this.setState({alreadyShowedCashflowOverrideWarning: true, showCashflowOverrideWarning: false});
  }

  translateAvailableCollaborationGroups = () => {
    return this.props.availableCollaborationGroups.map(x=>{return {value: x.id, label: x.company.name}});
  }

  renderPrimaryFunds = (values, isEditing) => {
    let isThisUserACollaborator = false;
    if(values && values.collaborationGroups) {
      values.collaborationGroups.forEach(x=>{
        if(this.props.authentication.user.contact.companyId === x.companyId) isThisUserACollaborator = true;
      })
    }
    isEditing = isEditing && !isThisUserACollaborator;
    return (
      <>
       <Grid item xs={5}>
              <StyledLabel isEditing={isEditing} text="Group Name(s)" />
            </Grid>
            <FundsSelectGrid isEditing={isEditing} id="fund-select-grid" item xs={7}>
              <FundSelectAndPercent
                fundId={values.funds[0].id}
                shouldShowPercentInput={+values.funds[0].fundDealBridge.allocationPercent !== 100 || isEditing}
                fundOptions={this.state.fundNames}
                isEditing={isEditing}
                index={0}
              />
              {values.funds &&
                values.funds.map((x, i) => {
                  if (i === 0) return null;
                  return (
                    <FundSelectAndPercent
                      key={x.id}
                      fundId={x.id}
                      shouldShowPercentInput={+x.fundDealBridge.allocationPercent !== 100 || isEditing}
                      fundOptions={this.state.fundNames}
                      isEditing={isEditing}
                      removeFund={this.removeFund}
                      index={i}
                    />
                  );
                })}
              {isEditing && <FundsAddButton disabled={!this.allFundsAssigned()} id="addAFundButton" title="add a group" onClick={this.addEmptyFund} />}
            </FundsSelectGrid>
      </>
    )
  }

  renderCollaborationGroups = (values, isEditing) => {
    return (
      <>
       <Grid item xs={5}>
              <StyledLabel isEditing={isEditing} text="Collaborator(s)" />
            </Grid>
            <FundsSelectGrid isEditing={isEditing} item xs={7}>
              {values.collaborationGroups &&
                values.collaborationGroups.map((x, i) => {
                  return (
                    <FundSelectAndPercent
                      isCollaborationGroup = {true}
                      key={x.id}
                      fundId={x.id}
                      shouldShowPercentInput={false}
                      fundOptions={this.translateAvailableCollaborationGroups()}
                      isEditing={isEditing}
                      removeFund={this.removeCollaborationGroup}
                      index={i}
                    />
                  );
                })}
              {isEditing && <FundsAddButton disabled={!this.allCollaborationGroupsAssigned()} id="addAFundButton" title="add collaborator" onClick={this.addEmptyCollaborationGroup} />}
            </FundsSelectGrid>
      </>
    )
  }

  render() {
    const { classes, handleChange, isEditing, values, setFieldValue, brokerContacts, authentication } = this.props;
    const { showStageField } = this.state;
    const { isSystemAdmin } = authentication;
    if (!this.props.values) return null;
    if (!values.funds) return null;
    
    let contacts = [];

    if (this.hasBrokerContacts(values.brokerCompanyId, brokerContacts)) {
      contacts = brokerContacts[values.brokerCompanyId].map(x => ({
        label: `${x.firstName ? x.firstName : ""} ${x.lastName ? x.lastName : ""}`,
        value: x.id
      }));
    }

    const isClosedOrSoldDeal = values.dealStatusId === CLOSED || values.dealStatusId === SOLD;

    const filteredDealStatusList = getFilteredDealStatuses(this.state.dealStatuses, this.props.values.dealStatusId);

    return (
      <>
        <SimpleInfoDialog 
          modalOpen = {this.state.showCashflowOverrideWarning && !this.state.alreadyShowedCashflowOverrideWarning}
          title = {"Notice"}
          message = {"Changing a deal's closing date will reset all overridden quote or loan cashflows"}
          handleClose = {this.closeCashfllowOverrideWarningModal}
          handleAccept = {this.closeCashfllowOverrideWarningModal}
        />
        <Grid item xs={12}>
          <div className={classes.heading}>Asset Details</div>
        </Grid>
        <Grid item xs={10}>
          <Divider />
        </Grid>
        <Grid container direction="row">
          <Grid container item xs={12}>
            <GridTextInput name="name" label="Deal Name" placeholder="Deal Name" value={values.name} onChange={handleChange} shouldShow disabled={!isEditing} />
          </Grid>
          <Grid container item xs={12}>
           {this.renderPrimaryFunds(values, isEditing)}
           {this.renderCollaborationGroups(values, isEditing)}
          </Grid>
          <Grid container item xs={12}>
            <GridTextInput
              name="jvPartner"
              label="JV Partner"
              placeholder="JV Partner"
              value={values.jvPartner}
              onChange={handleChange}
              shouldShow={isEditing || !!values.jvPartner}
              disabled={!isEditing}
            />
          </Grid>
          <Grid container item xs={12}>
            <GridTextInput
              name="internalEquityOwnership"
              label="Sponsor Equity Ownership"
              placeholder="Internal Equity Ownership"
              value={getMetric(values, INTERNAL_EQUITY_OWNERSHIP)}
              onChange={handleChange}
              shouldShow
              disabled
            />
          </Grid>
          <Grid container item xs={12}>
            <GridTextInput
              name="externalEquityOwnership"
              label="External Equity Ownership"
              placeholder="External Equity Ownership"
              value={getMetric(values, EXTERNAL_EQUITY_OWNERSHIP)}
              onChange={handleChange}
              shouldShow
              disabled
            />
          </Grid>
          <Grid container item xs={12}>
            <GridTextInput
              name="purchasePrice"
              label="Purchase Price"
              placeholder="Purchase Price"
              value={getMetric(values, PURCHASE_PRICE)}
              onChange={handleChange}
              shouldShow
              disabled
            />
          </Grid>

          <Grid container item xs={12}>
            <GridTextInput
              name="purchasePrice"
              label="Purchase Price"
              placeholder="Purchase Price"
              value={getMetric(values, PURCHASE_PRICE)}
              onChange={handleChange}
              shouldShow
              disabled
            />
          </Grid>
          <Grid container item xs={12}>
            <GridCurrencyInput
              name="cashEquity"
              label="Cash Equity"
              placeholder="Cash Equity"
              value={values.cashEquity}
              type="number"
              onBlur={item => {
                handleChange({
                  target: {
                    name: "cashEquity",
                    value: transformNumber(item.target.value)
                  }
                });
              }}
              onChange={handleChange}
              shouldShow={isEditing || !!values.cashEquity}
              disabled={!isEditing}
            />
          </Grid>
          {!authentication.isGuest && (
            <Grid container item xs={12}>
              <GridTextInput name="totalNOI" label="NOI" placeholder="NOI" value={getMetric(values, NOI)} onChange={handleChange} shouldShow disabled />
            </Grid>
          )}
          {!authentication.isGuest && (
            <Grid container item xs={12}>
              <GridTextInput
                name="totalOccupancy"
                label="Occupancy"
                placeholder="Occupancy"
                value={getMetric(values, OCCUPANCY)}
                onChange={handleChange}
                shouldShow
                disabled
              />
            </Grid>
          )}
          {!authentication.isGuest && (
            <Grid container item xs={12}>
              <GridTextInput
                name="capRate"
                label="Cap Rate"
                placeholder="Cap Rate"
                value={getMetric(values, CAP_RATE)}
                onChange={handleChange}
                shouldShow
                disabled
              />
            </Grid>
          )}
          <Grid container item xs={12}>
            <GridTextArea
              name="collateralization"
              label="Cross-Collateralized"
              placeholder="Cross-Collateralized"
              value={values.collateralization}
              onChange={handleChange}
              shouldShow={isEditing || !!values.collateralization}
              disabled={!isEditing}
            />
          </Grid>
          {/* Status dropdown is enabled for System Admins only */}
          <Grid container item xs={12} alignItems="center" id="status-select-grid">
            <Grid container item xs={12}>
              <Grid item xs={5}>
                <StyledLabel text="Status" />
              </Grid>
              <Grid item xs={5}>
                {!isEditing && <ViewModeDiv id="statusField">{this.getDealStatus(values.dealStatusId)}</ViewModeDiv>}
                {isEditing && <AutoCompleteInput label="" selectValue="value" options={filteredDealStatusList} fieldName="dealStatusId" isDisabled={!isSystemAdmin} />}
              </Grid>
            </Grid>
          </Grid>
          {showStageField && (
            <Grid container item xs={12} alignItems="center" id="stage-select-grid">
              <Grid item xs={5}>
                <StyledLabel text="Stage" />
              </Grid>
              <Grid item xs={5}>
                {!isEditing && <ViewModeDiv id="stageField">{this.getDealStage(values.stageId)}</ViewModeDiv>}
                {isEditing && (
                  <AutoCompleteInput
                    label=""
                    selectValue="value"
                    options={this.state.dealStages}
                    defaultValue={showStageField ? { value: `${this.getDealStage(STAGES.SOFT_QUOTE)}`, label: "Soft Quote" } : "value"}
                    fieldName="stageId"
                  />
                )}
              </Grid>
            </Grid>
          )}
          <Grid container item xs={12} alignItems="center" style={{ marginTop: 10 }} id="type-select-grid">
            <Grid item xs={5}>
              <StyledLabel text="Project Type" />
            </Grid>
            <Grid item xs={5}>
              {!isEditing && <ViewModeDiv id="typeField">{this.getProjectType(values.projectTypeId)}</ViewModeDiv>}
              {isEditing && <AutoCompleteInput label="" selectValue="value" options={this.state.projectTypes} fieldName="projectTypeId" />}
            </Grid>
          </Grid>
          {!isClosedOrSoldDeal && (
            <Grid container item xs={12} alignItems="center" style={{ marginTop: 10 }} id="targetedClosing-select-grid">
              <Grid item xs={5}>
                <StyledLabel text={values.targetedClosing ? `Targeted Close` : `Targeted Close*`} />
              </Grid>
              <Grid item xs={5}>
                {!isEditing && <ViewModeDiv id="targetedClosingField">{this.getFormattedDate(values.targetedClosing)}</ViewModeDiv>}
                {isEditing && (                  
                  <span onFocus={()=>{
                    this.setState({showCashflowOverrideWarning: true});
                  }}>
                    <GridDatePicker
                      name="targetedClosing"
                      label="Targeted Close"
                      placeholder="Targeted Close"
                      value={values.targetedClosing}
                      onChange={(event)=>{setFieldValue(event); }}
                      shouldShow={isEditing || !!values.targetedClosing}
                      disabled={!isEditing}
                      classes={this.props.classes.red}
                    />
                  </span>
                )}
              </Grid>
            </Grid>
          )}
          {!isClosedOrSoldDeal && (
            <Grid container item xs={12} style={{ paddingTop: "10px" }}>
              <GridCurrencyInput
                name="desiredLoanAmount"
                label="Desired Loan Amount"
                placeholder="Desired Loan Amount"
                value={values.desiredLoanAmount}
                type="number"
                onBlur={item => {
                  handleChange({
                    target: {
                      name: "desiredLoanAmount",
                      value: transformNumber(item.target.value)
                    }
                  });
                }}
                onChange={handleChange}
                shouldShow={isEditing || !!values.desiredLoanAmount}
                disabled={!isEditing}
              />
            </Grid>
          )}
          <Grid container item xs={12} style={{ marginTop: 10 }} id="brokerCompany-select-grid">
            <Grid item xs={5}>
              <StyledLabel text="Broker Company" />
            </Grid>
            <Grid item xs={5}>
              {!isEditing && <ViewModeDiv id="brokerCompanyField">{this.getBrokerCompany(values.brokerCompanyId)}</ViewModeDiv>}
              {isEditing && <AutoCompleteInput label="" selectValue="value" options={this.state.brokerCompanies} fieldName="brokerCompanyId" />}
            </Grid>
          </Grid>
          <Grid container item xs={12} style={{ marginTop: 10 }} id="brokerContact-select-grid">
            <Grid item xs={5}>
              <StyledLabel text="Broker Contact" />
            </Grid>
            <Grid item xs={5}>
              {!isEditing && <ViewModeDiv id="brokerContactField">{this.getBrokerContacts(values.brokerContactId)}</ViewModeDiv>}
              {isEditing && <AutoCompleteInput label="" selectValue="value" options={contacts} fieldName="brokerContactId" />}
            </Grid>
          </Grid>

          <Grid container item xs={12} alignItems="center" style={{ marginTop: 10 }} id="teamMember-select-grid">
            <Grid item xs={5}>
              <StyledLabel text="Team Member" />
            </Grid>
            <Grid item xs={5}>
              {!isEditing && <ViewModeDiv id="teamMemberField">{this.getConfigItemLabel(this.state.teamMembers, values.teamMemberId)}</ViewModeDiv>}
              {isEditing && <AutoCompleteInput label="" selectValue="value" options={this.state.teamMembers} fieldName="teamMemberId" />}
            </Grid>
          </Grid>
          <Grid container item xs={12} alignItems="center" style={{ marginTop: 10 }} id="assetManager-select-grid">
            <Grid item xs={5}>
              <StyledLabel text="Asset Manager" />
            </Grid>
            <Grid item xs={5}>
              {!isEditing && <ViewModeDiv id="assetManagerField">{this.getConfigItemLabel(this.state.teamMembers, values.assetManagerId)}</ViewModeDiv>}
              {isEditing && <AutoCompleteInput label="" selectValue="value" options={this.state.teamMembers} fieldName="assetManagerId" />}
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  }
}

const mapStateToProps = state => {
  const { loading, configItems, enumeratedTypes, contacts, authentication, fund } = state;
  return {
    authentication,
    loading,
    configItems,
    enumeratedTypes: enumeratedTypes.enumeratedTypes,
    brokerContacts: contacts.contactsByCompany,
    availableCollaborationGroups: fund.availableCollabGroups || [],
  };
};

export default compose(
  connect(mapStateToProps, {
    getConfigItemByParentId,
    getDealStages,
    getDealStatuses,
    getFundNames,
    getBrokers,
    getContactsByCompany,
    getAvailableCollabGroups,
    getDealCompanyContacts
  }),
  withStyles(styles)
)(ViewAndEditAssetDetails);
